import { useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { Download } from 'lucide-react';
import { Document, pdf } from '@react-pdf/renderer';
import PrintArbre, { getPrintHeaderVariant } from '@repo/print-pdf/arbre/print';
import { PdfPrintTemplate } from '@repo/print-pdf/arbre/pdf/template';
import { HtmlPrintTemplate, renderWebBlock } from '@repo/print-pdf/arbre/print/template';
import { PRINT_TEMPLATES_ENABLED, useEffectiveArbreTemplate } from '../../../../../features/print-templates/template-source';
import { useInventaire } from '@repo/ui/features/inventaires/hooks/use-inventaire';
import { usePrintData } from '@repo/ui/features/inventaires/hooks/use-print-data';
import { usePDFDownload } from '@repo/ui/features/inventaires/print/hooks/use-pdf-download';
import { PDFDocument } from '@repo/print-pdf';

export default function ArbrePrintPage() {
  const { id = '' } = useParams<{ id?: string }>();
  const inventaireId = Number(id);
  const { data: inventaireData, isLoading, error } = useInventaire(inventaireId);

  // Try to use new backend endpoint for print data (faster)
  const { data: printDataFromBackend, error: printDataError, isLoading: isLoadingPrintData } = usePrintData(inventaireId);
  const printData = printDataFromBackend;

  // Fiche pilotée par le modèle de l'organisation (désactivable avec VITE_PRINT_TEMPLATES=false) : réservée aux arbres, les alignements gardent leur fiche.
  const { template, isLoading: isLoadingTemplate } = useEffectiveArbreTemplate();
  const useTemplate = PRINT_TEMPLATES_ENABLED && getPrintHeaderVariant((printData as { inventaireType?: string } | undefined)?.inventaireType) === 'primary';

  const generatePDF = useMemo(() => {
    return async (): Promise<Blob> => {
      if (!printData) {
        throw new Error('Données non disponibles pour la génération du PDF');
      }

      // Validate that printData has required fields
      if (!printData.organizationName) {
        throw new Error('Données d\'inventaire incomplètes');
      }

      try {
        // PDFDocument already returns a <Document>, so we don't need to wrap it
        const blob = await pdf(
          <Document>
            {useTemplate ? (
              <PdfPrintTemplate template={template} data={printData as any} inventaireId={inventaireId} />
            ) : (
              <PDFDocument data={printData as any} inventaireId={inventaireId} />
            )}
          </Document>
        ).toBlob();

        if (!blob) {
          throw new Error('Impossible de générer le blob PDF');
        }

        return blob;
      } catch (error) {
        console.error('Erreur lors de la génération du PDF:', error);
        throw error instanceof Error ? error : new Error('Erreur inconnue lors de la génération du PDF');
      }
    };
  }, [printData, inventaireId, useTemplate, template]);

  // Determine file type label based on inventaire type
  const fileTypeLabel = useMemo(() => {
    if (!inventaireData?.data?.type) {
      return 'arbre';
    }
    const type = inventaireData.data.type.toUpperCase();
    if (type === 'ALIGNEMENT') {
      return 'alignement';
    }
    return 'arbre';
  }, [inventaireData?.data?.type]);

  const { isGeneratingPDF, handleDownloadPDF } = usePDFDownload(generatePDF, {
    filename: `${fileTypeLabel}-${inventaireId || 'fiche'}.pdf`,
    onError: (error) => {
      console.error('Erreur génération PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error.message}`);
    },
  });

  if (isLoading || isLoadingPrintData || isLoadingTemplate) {
    return <div>Chargement de l'inventaire...</div>;
  }
  if (error || !printData) {
    return (
      <div>
        <p>Erreur lors du chargement des données</p>
        {error && <p>Erreur inventaire: {error.message}</p>}

        {!printData && <p>Données d'impression non disponibles</p>}
      </div>
    );
  }

  return (
    <div className="w-full py-6">
      {/* PDF Download Button - Fixed position like PrintArbre button */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={handleDownloadPDF}
          disabled={isGeneratingPDF}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={18} />
          <span>{isGeneratingPDF ? 'Génération...' : 'Télécharger PDF'}</span>
        </button>
      </div>
      {useTemplate ? (
        <div className="bg-gray-100 min-h-screen">
          <HtmlPrintTemplate
            template={template}
            data={printData as any}
            inventaireId={inventaireId}
            pageClassName="px-[200px] 2xl:px-[400px]"
            renderBlock={renderWebBlock(printData as any)}
          />
        </div>
      ) : (
        <PrintArbre printData={printData as any} inventaireId={inventaireId} isMobile={false} />
      )}
    </div>
  );
}
